//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import { RouterProvider } from 'react-router/dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from '@/services/queryClient';
import { router } from '@/routes';
import { ThemeProvider } from '@/theme/ThemeProvider';
import AuthProvider from '@/contexts/AuthProvider';
import ConfigProvider from '@/contexts/ConfigProvider';
import DatasetProvider from '@/contexts/DatasetProvider';
import QueryProvider from '@/contexts/QueryProvider';
import LLMProvider from '@/contexts/LLMProvider';
import UIProvider from '@/contexts/UIProvider';
import AppBootstrap from '@/AppBootstrap';
import PageSnapshotProvider from '@/agent/PageSnapshotProvider';
import ChatProvider from '@/contexts/ChatProvider';
import PanelProvider from '@/contexts/PanelProvider';
import PanelHost from '@/components/Panels/PanelHost';
import PanelErrorBoundary from '@/components/Panels/PanelErrorBoundary';
import ChatPanelClient from '@/components/Chat/ChatPanelClient';
import PluginPanelClients from '@/components/Panels/PluginPanelClients';
import { SnackbarProvider } from '@/contexts/SnackbarProvider';
import { HelpProvider } from '@/contexts/HelpProvider';
import { SettingsProvider } from '@/contexts/SettingsProvider';
import TargetSelectorProvider from '@/contexts/TargetSelectorProvider';
import SessionTimeout from '@/components/Session/SessionTimeout';

// App is the application shell: it composes the context providers, runs the
// one-time startup orchestration (AppBootstrap), and renders the router.  Order
// matters: QueryClientProvider (TanStack Query) is outermost so every
// provider/hook can use the query cache; the domain providers need
// ThemeProvider (useTheme via the error handler), and AppBootstrap needs
// Auth/Config/LLM (useAuth/useConfig/useLLM) for its startup effect.
// QueryProvider here is the app's own query *slice* (question/query state),
// distinct from QueryClientProvider.
// PanelProvider and PanelHost are siblings of the router, not children of a
// page: a route change must not unmount a floating panel, since unmounting a
// plugin surface's iframe reloads its document.  Being siblings is also why
// PanelErrorBoundary wraps them and not the router: outside the route tree
// there is no errorElement to catch a rejected plugin manifest, and a
// manifest rejection must cost the panels, not blank every route.
// PageSnapshotProvider wraps both for the same reason: a page publishes its
// snapshot and registers its actions from inside the router, and the chat
// panel body (under PanelHost) reads both, so the bridge must be an ancestor
// of each.
const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider>
      <AuthProvider>
        <ConfigProvider>
          <DatasetProvider>
            <QueryProvider>
              <LLMProvider>
                <UIProvider>
                  <AppBootstrap>
                    <ChatProvider>
                      <SnackbarProvider>
                        <HelpProvider>
                          <SettingsProvider>
                            <TargetSelectorProvider>
                              <PanelProvider>
                                <PageSnapshotProvider>
                                  <SessionTimeout />
                                  <RouterProvider router={router} />
                                  <PanelErrorBoundary>
                                    <ChatPanelClient />
                                    <PluginPanelClients />
                                    <PanelHost />
                                  </PanelErrorBoundary>
                                </PageSnapshotProvider>
                              </PanelProvider>
                            </TargetSelectorProvider>
                          </SettingsProvider>
                        </HelpProvider>
                      </SnackbarProvider>
                    </ChatProvider>
                  </AppBootstrap>
                </UIProvider>
              </LLMProvider>
            </QueryProvider>
          </DatasetProvider>
        </ConfigProvider>
      </AuthProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
